import styles from './Role.module.css';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Clear from '@mui/icons-material/Clear';
import Delete from '@mui/icons-material/Delete';
import Divider from '@mui/material/Divider';
import Edit from '@mui/icons-material/Edit';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Save from '@mui/icons-material/Save';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useEffect, useState } from 'react';

const EditRole = ({ role, certifications, onAddRole, onUpdateRole, onCancel }) => {
    const [ editRole, setEditRole ] = useState(role);

    const handleOnChange = (e) => {
        const { name, value, type, checked } = e.target;
        const fieldValue = type === 'checkbox' ? checked : value;

        setEditRole({ ...editRole, [ name ]: fieldValue });
    };

    const handleSaveRole = () => {
        if (editRole.id === 'new') {
            onAddRole(editRole);
        } else {
            onUpdateRole(editRole); 
        }
    };

    return (
        <Box spacing={ 2 } sx={ { width: '100%' } }>
            <input type="hidden" id="roleId" name="id" value={ role.id } />

            <Divider sx={ { my: 2 } } />

            <Grid container spacing={ 1 } sx={ { width: '100%' } }>
                <Grid sx={ { px: 1 } } size={ 4 } display='flex' alignItems='center'>
                    <TextField
                        id="roleName"
                        name="name"
                        label="Name"
                        value={ editRole.name || '' }
                        onChange={ handleOnChange }
                        sx={{ width: '100%' }}
                    />
                </Grid>

                <Grid sx={ { px: 1 } } size={ 4 } display='flex' alignItems='center'>
                    <FormControl fullWidth>
                        <InputLabel id="roleCertificationLabel">Certification</InputLabel>

                        <Select
                            labelId="roleCertificationLabel"
                            id="roleCertification"
                            name="certification"
                            value={ editRole.certification || "none" }
                            onChange={ handleOnChange }
                            label="Certification"
                            MenuProps={{ disableScrollLock: true }}
                        >
                            <MenuItem value="none">
                                <em>None</em>
                            </MenuItem>

                            { certifications.map(cert => cert.type === 'primary' && (
                                <MenuItem key={ cert.id } value={ cert.id }>{ cert.name }</MenuItem>
                            )) }
                        </Select>
                    </FormControl>
                </Grid>

                <Grid sx={ { px: 1 } } size={ 4 } display='flex' alignItems='center'>
                    <FormControl fullWidth>
                        <InputLabel id="roleRequiredLabel">Required</InputLabel>

                        <Select
                            labelId="roleRequiredLabel"
                            id="roleRequired"
                            name="required"
                            value={ editRole.required || "no" }
                            onChange={ handleOnChange }
                            label="Required"
                            MenuProps={{ disableScrollLock: true }}
                        >
                            <MenuItem value="no">No</MenuItem>
                            <MenuItem value="required">Required</MenuItem>
                            <MenuItem value="recommended">Recommended</MenuItem>
                            <MenuItem value="optional">Optional</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid sx={ { px: 1 } } size={ 12 } display='flex' alignItems='center'>
                    <FormControlLabel
                        control={
                            <Switch name="lead" checked={ editRole.lead } onChange={ handleOnChange } />
                        }
                        label="This is an elevated role (e.g. hill captain, shift lead, shift coordinator, etc.)"
                    />

                </Grid>
            </Grid >

            <Divider sx={ { my: 2 } } />

            <div className={ styles.actions }>
                <Button variant="outlined" color="secondary" onClick={ onCancel } startIcon={ <Clear /> }>Cancel</Button>
                <Button variant="contained" color="primary" onClick={ handleSaveRole } disabled={ !editRole.name } startIcon={ <Save /> }>Save Role</Button>
            </div>
        </Box>
    )
};

const ShowRole = ({ role, certifications, readOnly, onEditRole, onRemoveRole }) => {
    const handleEditRole = () => {
        onEditRole(role);
    };

    const handleRemoveRole = () => {
        onRemoveRole(role);
    };

    return (
        <Grid container spacing={ 1 } sx={ { width: '100%' } }>
            <Grid sx={ { px: 1 } } size={ readOnly ? 3 : 2 } display='flex' alignItems='center'>
                <Typography variant="body1">{ role.name }</Typography>
            </Grid>

            <Grid sx={ { px: 1 } } size={ readOnly ? 4 : 3 } display='flex' alignItems='center'>
                <Typography variant="body1">{ certifications.find(c => c.id === role.certification)?.name || "None" }</Typography>
            </Grid>

            <Grid sx={ { px: 1 } } size={ readOnly ? 2 : 2 } display='flex' alignItems='center'>
                <Typography variant="body1">{ role.required || "No" }</Typography>
            </Grid>

            <Grid sx={ { px: 1 } } size={ readOnly ? 3 : 3 } display='flex' alignItems='center'>
                <Typography variant="body1">{ role.lead ? "Yes" : "No" }</Typography>
            </Grid>

            <Grid sx={ { px: 1 } } size={ 2 } display={ readOnly ? 'none' : 'flex' } justifyContent='flex-end' alignItems='center'>
                <IconButton variant="outlined" color="primary" onClick={ handleEditRole } ><Edit /></IconButton>
                <IconButton variant="outlined" color="error" onClick={ handleRemoveRole } ><Delete /></IconButton>
            </Grid>
        </Grid>
    )
};

export const Role = ({ role, certifications, readOnly, onAddRole, onUpdateRole, onRemoveRole, onCancel }) => {
    const [ editRole, setEditRole ] = useState(null);

    const handleUpdateRole = (r) => {
        onUpdateRole(r);

        setEditRole(null);
    };

    const handleCancel = () => {
        setEditRole(null);

        onCancel();
    };

    useEffect(() => {
        setEditRole(role.id === 'new' ? { ...role } : null);
    }, [ role ]);

    return (
        editRole && !readOnly ?
            <EditRole role={ editRole || role } certifications={ certifications } onAddRole={ onAddRole } onUpdateRole={ handleUpdateRole } onCancel={ handleCancel } />
            :
            <ShowRole role={ role } certifications={ certifications } readOnly={ readOnly } onEditRole={ setEditRole } onRemoveRole={ onRemoveRole } />
    );
};